import { useState, useMemo } from "react";
import {
  ThemeProvider, CssBaseline, Box, Typography, IconButton,
  useMediaQuery, Drawer, Tooltip,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import TrainIcon from "@mui/icons-material/Train";
import DirectionsTransitIcon from "@mui/icons-material/DirectionsTransit";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import { Analytics } from "@vercel/analytics/react";
import { buildTheme } from "./theme";
import DispatchPage from "./pages/DispatchPage";
import RaccoonPage from "./pages/RaccoonPage";
import TransitPage from "./pages/TransitPage";
import GOSchedulePage from "./pages/GOSchedulePage";
import CalendarPage from "./pages/CalendarPage";
import InfoPage from "./pages/InfoPage";
import ChangelogPage from "./pages/ChangelogPage";
import SettingsPage, { loadSettings, saveSettings } from "./pages/SettingsPage";
import WeatherPage from "./pages/WeatherPage";

function RaccoonIcon({ size = 17, color = "currentColor" }) {
  const base = color === "currentColor" ? "#94a3b8" : color;
  const dk = "#1e293b";
  const mid = "#475569";
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      <ellipse cx="16" cy="17" rx="12" ry="11" fill={base} />
      <ellipse cx="8" cy="9" rx="5" ry="6" fill={base} />
      <ellipse cx="24" cy="9" rx="5" ry="6" fill={base} />
      <ellipse cx="8" cy="8" rx="3" ry="4" fill={dk} />
      <ellipse cx="24" cy="8" rx="3" ry="4" fill={dk} />
      <ellipse cx="10" cy="19" rx="5" ry="4" fill={mid} />
      <ellipse cx="22" cy="19" rx="5" ry="4" fill={mid} />
      <ellipse cx="16" cy="20" rx="3" ry="2" fill="#f8fafc" />
      <ellipse cx="14" cy="22" rx="1.5" ry="1" fill={dk} />
      <ellipse cx="18" cy="22" rx="1.5" ry="1" fill={dk} />
      <ellipse cx="16" cy="23.5" rx="2" ry="1.2" fill={base} />
      <ellipse cx="9" cy="17" rx="1.2" ry="1" fill={dk} />
      <ellipse cx="23" cy="17" rx="1.2" ry="1" fill={dk} />
    </svg>
  );
}

const SIDEBAR_W = 200;

const NAV_ITEMS = [
  { id: "go",       label: "GO Schedule",         section: "Live",      icon: DirectionsTransitIcon, dot: false },
  { id: "transit",  label: "Transit Status",       section: "Live",      icon: TrainIcon,             dot: false },
  { id: "dispatch", label: "Police & Fire",         section: "Live",      icon: ShieldIcon,            dot: false },
  { id: "weather",  label: "Weather & Pollen",      section: "City Data", icon: WbSunnyIcon,           dot: false },
  { id: "calendar", label: "Calendar",              section: "City Data", icon: CalendarMonthIcon,     dot: false },
  { id: "raccoons", label: "Animal Sightings",      section: "City Data", icon: null, renderIcon: (color) => <RaccoonIcon size={20} color={color} />, dot: false },
  { id: "info",     label: "Info & Disclaimer",     section: "MAIN // DANFORTH", icon: InfoOutlinedIcon, dot: false },
  { id: "changelog", label: "Changelog",               section: "MAIN // DANFORTH", icon: HistoryIcon,      dot: false },
  { id: "settings",  label: "Settings",                 section: "MAIN // DANFORTH", icon: SettingsIcon,     dot: false },
];

function NavItem({ item, active, dark, border, onClick }) {
  const Icon = item.icon;
  const iconColor = active ? (dark ? "#f1f5f9" : "#0f172a") : (dark ? "#64748b" : "#94a3b8");
  return (
    <Box onClick={onClick} sx={{
      display: "flex", alignItems: "center", gap: 1.25,
      px: 1.75, py: 1.1, cursor: "pointer",
      borderLeft: `2px solid ${active ? "#3b82f6" : "transparent"}`,
      bgcolor: active ? (dark ? "#1e293b" : "#eff6ff") : "transparent",
      color: active ? (dark ? "#f1f5f9" : "#0f172a") : (dark ? "#64748b" : "#94a3b8"),
      "&:hover": { bgcolor: dark ? "#1e293b88" : "#f1f5f9", color: dark ? "#f1f5f9" : "#0f172a" },
      transition: "all 0.15s", userSelect: "none",
    }}>
      {item.renderIcon ? item.renderIcon(iconColor) : <Icon sx={{ fontSize: 20, flexShrink: 0 }} />}
      <Typography sx={{ fontSize: "0.94rem", fontWeight: active ? 700 : 500, flex: 1, whiteSpace: "nowrap" }}>
        {item.label}
      </Typography>
      {item.dot && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#10b981", flexShrink: 0, animation: "pulse 2s infinite" }} />}
    </Box>
  );
}

function Sidebar({ activePage, setActivePage, dark, border, barBg, colorMode, setColorMode, onClose }) {
  const sections = NAV_ITEMS.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ width: SIDEBAR_W, height: "100%", display: "flex", flexDirection: "column", bgcolor: barBg }}>
      <Box sx={{ px: 1.75, height: 50, display: "flex", alignItems: "center", gap: 1, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
        <Box onClick={() => setActivePage(loadSettings().defaultPage || "go")} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 0.75, bgcolor: "#D52B1E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 9, color: "#fff", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.5px" }}>M//D</Typography>
          </Box>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>MAIN // DANFORTH</Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary", mr: -0.5 }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {Object.entries(sections).map(([section, items]) => (
          <Box key={section}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: dark ? "#334155" : "#cbd5e1", px: 1.75, py: 0.75 }}>
              {section}
            </Typography>
            {items.map(item => (
              <NavItem key={item.id} item={item} active={activePage === item.id} dark={dark} border={border}
                onClick={() => { setActivePage(item.id); onClose && onClose(); }} />
            ))}
          </Box>
        ))}
      </Box>
      <Box sx={{ borderTop: `1px solid ${border}`, px: 1.75, height: 52, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <Typography sx={{ fontSize: "1.1rem", color: "text.secondary", flex: 1 }}>
          {dark ? "Dark mode" : "Light mode"}
        </Typography>
        <Tooltip title={dark ? "Switch to light" : "Switch to dark"}>
          <IconButton size="small" onClick={() => setColorMode(dark ? "light" : "dark")} sx={{ color: "text.secondary" }}>
            {dark ? <LightModeIcon sx={{ fontSize: 16 }} /> : <DarkModeIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function App() {
  const [userSettings, setUserSettings] = useState(() => loadSettings());
  const [colorMode, setColorMode] = useState(() => loadSettings().colorMode || "dark");
  const [activePage, setActivePage] = useState(() => loadSettings().defaultPage || "go");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const dark = colorMode === "dark";

  // Persist colorMode changes
  const handleColorMode = (mode) => {
    setColorMode(mode);
    const next = { ...userSettings, colorMode: mode };
    setUserSettings(next);
    saveSettings(next);
  };
  const theme = useMemo(() => buildTheme(colorMode), [colorMode]);
  const border = dark ? "#1e293b" : "#e2e8f0";
  const barBg = dark ? "#0a0e1a" : "#ffffff";
  const isMobile = useMediaQuery("(max-width:768px)");

  const sidebarProps = { activePage, setActivePage, dark, border, barBg, colorMode, setColorMode };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${dark ? "#1e293b" : "#e2e8f0"}; border-radius: 2px; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        .leaflet-popup-content-wrapper { background:#fff; border-radius:8px; box-shadow:0 4px 20px rgba(0,0,0,0.15); padding:2px; }
        .leaflet-popup-tip { background:#fff; }
        .leaflet-container { font-family:'Outfit',sans-serif; }
        .leaflet-control-zoom a { background:${dark ? "#111827" : "#fff"} !important; color:${dark ? "#94a3b8" : "#475569"} !important; border-color:${border} !important; }
        .leaflet-control-zoom a:hover { background:${dark ? "#1e293b" : "#f1f5f9"} !important; }
        .leaflet-control-attribution { background:${dark ? "#0a0e1acc" : "#ffffffcc"} !important; color:${dark ? "#334155" : "#94a3b8"} !important; font-size:0.6rem !important; }
        .leaflet-control-attribution a { color:${dark ? "#475569" : "#64748b"} !important; }
        .division-tooltip { background: ${dark ? "#111827" : "#fff"}; border: 1px solid ${border}; color: ${dark ? "#f1f5f9" : "#0f172a"}; font-family: 'Outfit', sans-serif; font-size: 0.75rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; box-shadow: none; }
        .division-tooltip::before { display: none; }
        .leaflet-interactive { cursor: pointer; }
        ${dark ? `.leaflet-pane.leaflet-shadow-pane { filter: brightness(2.2) contrast(1.1); }` : ""}
      `}</style>

      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        {!isMobile && (
          <Box sx={{ width: SIDEBAR_W, flexShrink: 0, borderRight: `1px solid ${border}`, height: "100vh" }}>
            <Sidebar {...sidebarProps} onClose={null} />
          </Box>
        )}
        {isMobile && (
          <Drawer anchor="left" open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR_W, bgcolor: barBg, border: "none" } }}>
            <Sidebar {...sidebarProps} onClose={() => setMobileNavOpen(false)} />
          </Drawer>
        )}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
          {isMobile && (
            <Box sx={{ height: 50, display: "flex", alignItems: "center", px: 1.5, gap: 1, borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
              <IconButton size="small" onClick={() => setMobileNavOpen(true)} sx={{ color: "text.secondary" }}>
                <MenuIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box onClick={() => setActivePage(loadSettings().defaultPage || "go")} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
                <Box sx={{ width: 22, height: 22, borderRadius: 0.75, bgcolor: "#D52B1E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 9, color: "#fff", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.5px" }}>M//D</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>MAIN // DANFORTH</Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            {activePage === "dispatch" && (
              <DispatchPage colorMode={colorMode} setColorMode={handleColorMode} dark={dark} border={border} barBg={barBg} isMobile={isMobile} defaultDivision={userSettings.defaultDivision || "D55"} />
            )}
            {activePage === "transit" && (
              <TransitPage colorMode={colorMode} setColorMode={setColorMode} dark={dark} border={border} barBg={barBg} isMobile={isMobile} />
            )}
            {activePage === "go" && (
              <GOSchedulePage colorMode={colorMode} setColorMode={setColorMode} dark={dark} border={border} barBg={barBg} isMobile={isMobile} />
            )}
            {activePage === "weather" && (
              <WeatherPage colorMode={colorMode} setColorMode={setColorMode} dark={dark} border={border} barBg={barBg} isMobile={isMobile} />
            )}
            {activePage === "changelog" && (
              <ChangelogPage dark={dark} border={border} barBg={barBg} />
            )}
            {activePage === "settings" && (
              <SettingsPage dark={dark} border={border} barBg={barBg}
                settings={{ ...userSettings, colorMode }}
                onSettingsChange={(next) => {
                  setUserSettings(next);
                  if (next.colorMode) setColorMode(next.colorMode);
                }} />
            )}
            {activePage === "calendar" && (
              <CalendarPage colorMode={colorMode} setColorMode={setColorMode} dark={dark} border={border} barBg={barBg} isMobile={isMobile} />
            )}
            {activePage === "info" && (
              <InfoPage dark={dark} border={border} barBg={barBg} isMobile={isMobile} />
            )}
            {activePage === "raccoons" && (
              <RaccoonPage colorMode={colorMode} setColorMode={setColorMode} dark={dark} border={border} barBg={barBg} isMobile={isMobile} />
            )}
          </Box>
        </Box>
      </Box>
      <Analytics />
    </ThemeProvider>
  );
}
